<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import Icon from "@/design/primitives/Icon.vue";
import { UPLOAD_PORT } from "@/shared/contracts/ports";
import { say } from "../say";
import type { WidgetProps } from "./contract";

/**
 * An attachment.
 *
 * `config` carries the capability rather than the type doing it: `multiple`,
 * `accept` and `max_mb` are configuration, so a "rich file" is not a second
 * field type (§4.2).
 *
 * The bytes go through the platform's ONE write path and the field stores an id
 * — never a URL. A URL is a byte we do not own, and it expires (I-S5).
 *
 * ── COMO N ARQUIVOS CABEM NUMA COLUNA SÓ (§2.6 #15) ───────────────────────
 *
 * O valor é uma STRING de uuids separados por vírgula, e um uuid solto continua
 * sendo o caso de um elemento — é assim que tudo que já está gravado continua
 * abrindo. Não é um array JS: a coluna de um campo `file` é `varchar(1024)` e o
 * `coerce()` do repositório manda `file` para o caminho `default`, então um array
 * viraria literal de array do Postgres e a escrita falharia. O formulário público
 * valida id a id no mesmo formato.
 */
const props = defineProps<
	WidgetProps & {
		ownerKind?: string;
		ownerId?: string;
		controlId?: string;
		describedBy?: string;
		/** Só mostrar — a ficha: as fotos em miniatura, sem enviar, remover nem reordenar. */
		readonly?: boolean;
	}
>();
const emit = defineEmits<{ "update:modelValue": [unknown]; "update:busy": [boolean]; blur: [] }>();

const uploads = inject(UPLOAD_PORT, null);
const busy = ref(false);
watch(busy, (value) => emit("update:busy", value), { flush: "sync" });
const failure = ref("");
/** O mime vem do próprio arquivo, e não do `accept` do campo: uma galeria mistura foto e vídeo. */
const previews = ref<Array<{ id: string; url: string | null; mime: string; name: string }>>([]);

const accept = computed(() =>
	typeof props.field.config.accept === "string" ? props.field.config.accept : undefined,
);
/**
 * O padrão é o teto do endpoint PÚBLICO, e não um número maior.
 *
 * Era 25, acima dos 10 MB que `public-upload.ts` recusa. O visitante escolhia um
 * arquivo de 15 MB, o widget aceitava, esperava a subida inteira numa conexão de
 * celular e só então o servidor recusava. Um teto local acima do real não protege
 * ninguém — só adia a recusa. Um campo pode elevá-lo por `config.max_mb` quando o
 * formulário for do painel, onde o teto de verdade é outro.
 */
const maxMb = computed(() => Number(props.field.config.max_mb ?? 10));
const multiple = computed(() => props.field.config.multiple === true);

/**
 * Quantos anexos cabem — pela largura da coluna, e não por gosto.
 *
 * `varchar(1024)` dividido por 37 (36 do uuid mais a vírgula) dá 27. Recusado
 * antes da requisição pelo mesmo motivo que o tamanho: subir o 28º para o banco
 * truncar a lista é perder um arquivo sem dizer qual.
 */
const MAX_FILES = 27;

const ids = computed(() => splitIds(props.modelValue));

function splitIds(value: unknown): string[] {
	if (value === null || value === undefined) return [];
	return String(value)
		.split(",")
		.map((id) => id.trim())
		.filter(Boolean);
}

/** A string que vai para a coluna. Lista vazia é `null`, nunca "". */
function emitIds(next: readonly string[]): void {
	emit("update:modelValue", next.length === 0 ? null : next.join(","));
}

watch(
	ids,
	async (list) => {
		previews.value = await Promise.all(
			list.map(async (id) => {
				const file = uploads ? await uploads.urlFor(id).catch(() => null) : null;
				return {
					id,
					url: file?.url ?? null,
					mime: file?.mimeType ?? "",
					name: file?.fileName ?? "",
				};
			}),
		);
	},
	{ immediate: true },
);

async function choose(event: Event): Promise<void> {
	const input = event.target as HTMLInputElement;
	const chosen = Array.from(input.files ?? []);
	if (chosen.length === 0) return;
	if (!uploads) {
		failure.value = say("uploadUnavailable");
		return;
	}

	// Uma pergunta de arquivo único TROCA o anexo; uma de N acumula.
	const kept = multiple.value ? [...ids.value] : [];
	const wanted = multiple.value ? chosen : chosen.slice(0, 1);

	if (kept.length + wanted.length > MAX_FILES) {
		failure.value = `cabem no máximo ${MAX_FILES} arquivos nesta pergunta`;
		input.value = "";
		return;
	}

	// Por arquivo, e antes de qualquer requisição: um de 15 MB no meio de três não
	// pode subir os outros dois e só então descobrir que o terceiro é grande demais.
	const tooBig = wanted.find((file) => file.size > maxMb.value * 1024 * 1024);
	if (tooBig) {
		failure.value = `"${tooBig.name}" é maior que ${maxMb.value} MB`;
		input.value = "";
		return;
	}

	busy.value = true;
	failure.value = "";
	try {
		for (const file of wanted) {
			const stored = await uploads.upload({
				blob: file,
				filename: file.name,
				ownerKind: props.ownerKind ?? "record",
				ownerId: props.ownerId ?? null,
				purpose: "attachment",
			});
			kept.push(stored.id);
			// Emitido a cada arquivo: se o quarto falhar, os três que já subiram estão
			// no valor em vez de virarem bytes órfãos que ninguém aponta.
			emitIds(kept);
		}
	} catch (e) {
		failure.value = e instanceof Error ? e.message : say("uploadFailed");
	} finally {
		busy.value = false;
		// Zerado para que escolher o MESMO arquivo de novo ainda dispare o `change`.
		input.value = "";
	}
}

function removeAt(id: string): void {
	emitIds(ids.value.filter((kept) => kept !== id));
}

/**
 * A ORDEM É DO DONO (28/09/2026): o primeiro é a capa do item no site e na
 * lista, e a galeria pública segue esta ordem. Trocar com o vizinho basta —
 * arrastar seria um segundo gesto para o mesmo resultado numa lista de ≤27.
 */
function isImage(item: { mime: string }): boolean {
	return item.mime.startsWith("image/") || (!item.mime && Boolean(accept.value?.includes("image")));
}

function move(index: number, delta: -1 | 1): void {
	const next = [...ids.value];
	const target = index + delta;
	const a = next[index];
	const b = next[target];
	if (a === undefined || b === undefined) return;
	next[index] = b;
	next[target] = a;
	emitIds(next);
}
</script>

<template>
	<div class="space-y-2">
		<!-- Só leitura: as fotos lado a lado, maiores, e a foto é o link — o nome guardado
		     ("0b66…-foto1.jpg") não diz nada a quem olha a ficha. -->
		<ul v-if="previews.length" :class="readonly ? 'flex flex-wrap gap-2' : 'space-y-1.5'">
			<li v-for="(item, index) in previews" :key="item.id" class="flex items-center gap-3">
				<a v-if="readonly && item.url && isImage(item)" :href="item.url" target="_blank" rel="noopener">
					<img :src="item.url" :alt="item.name" class="size-24 rounded-md border border-border object-cover" />
				</a>
				<img
					v-else-if="item.url && isImage(item)"
					:src="item.url"
					alt=""
					class="size-16 rounded-md border border-border object-cover"
				/>
				<video
					v-else-if="item.url && item.mime.startsWith('video/')"
					:src="item.url"
					preload="metadata"
					muted
					class="size-16 rounded-md border border-border bg-black object-cover"
				/>
				<a
					v-if="item.url && !(readonly && isImage(item))"
					:href="item.url"
					target="_blank"
					rel="noopener"
					class="min-w-0 flex-1 truncate text-sm text-accent underline"
				>
					{{ item.name || "ver arquivo" }}
				</a>
				<span v-else-if="!item.url" class="min-w-0 flex-1 truncate text-sm text-fg-subtle">arquivo</span>
				<template v-if="!readonly && multiple && previews.length > 1">
					<button
						type="button"
						class="text-xs text-fg-subtle disabled:opacity-40"
						:disabled="disabled || index === 0"
						:aria-label="say('fileMoveEarlier')"
						:title="say('fileMoveEarlier')"
						@click="move(index, -1)"
					>
						<Icon name="arrowUp" class="size-3.5" />
					</button>
					<button
						type="button"
						class="text-xs text-fg-subtle disabled:opacity-40"
						:disabled="disabled || index === previews.length - 1"
						:aria-label="say('fileMoveLater')"
						:title="say('fileMoveLater')"
						@click="move(index, 1)"
					>
						<Icon name="arrowDown" class="size-3.5" />
					</button>
				</template>
				<!-- O remover é POR ITEM: numa pergunta de três fotos, tirar a errada
				     não pode custar as outras duas. -->
				<button
					v-if="!readonly"
					type="button"
					class="text-xs text-fg-subtle underline"
					:disabled="disabled"
					@click="removeAt(item.id)"
				>
					remover
				</button>
			</li>
		</ul>

		<!--
			O único controle que não usa a receita do `control.ts`: um `<input
			type=file>` é um botão desenhado pelo navegador, com o seu próprio
			pseudoelemento. Emprestar-lhe a moldura de uma caixa de texto desenharia
			uma borda em volta de nada.
		-->
		<input
			v-if="!readonly"
			:id="controlId"
			type="file"
			:accept="accept"
			:multiple="multiple"
			:disabled="disabled || busy"
			:aria-invalid="Boolean(error)"
			:aria-describedby="describedBy"
			class="block w-full text-xs text-fg-muted file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-1.5 file:text-xs file:text-fg"
			@change="choose"
		/>
		<p v-if="busy" class="text-xs text-fg-subtle">enviando…</p>
		<p v-if="failure" class="text-xs text-danger">{{ failure }}</p>
	</div>
</template>
